/* Fast Media Boost (Elevate)
 * 1) IMAGES: render fast on load. Async-decode every image, eager-load + high
 *    priority for anything in (or near) the first viewport, and promote lazy
 *    data-src images shortly before they scroll into view. Also catches images
 *    added dynamically (cart drawer, block sections, AJAX) so they appear
 *    instantly instead of popping in.
 * 2) VIDEOS: make every video autoplay reliably (muted, inline), recover from
 *    browser autoplay blocks, and pause off-screen to keep the page fast.
 *
 * Purely additive: it does NOT change markup, layout, or design.
 * Opt out per element with `data-no-fast` (img) or `data-no-autoplay` (video).
 */
(function () {
  'use strict';

  var NEAR_VIEW_PX = 600;        // promote a little before it enters view
  var EAGER_FACTOR = 1.4;        // treat first ~1.4 viewports as "above the fold"
  var imgIO = null, vidIO = null;
  var pending = [];              // videos blocked by autoplay policy

  /* ---------------- IMAGES ---------------- */

  function vh() {
    return window.innerHeight || document.documentElement.clientHeight || 800;
  }

  function promoteImgSources(img) {
    var changed = false;
    if (img.dataset.src && !img.getAttribute('src')) {
      img.setAttribute('src', img.dataset.src); changed = true;
    }
    if (img.dataset.srcset && !img.getAttribute('srcset')) {
      img.setAttribute('srcset', img.dataset.srcset); changed = true;
    }
    var picture = img.parentNode;
    if (picture && picture.tagName === 'PICTURE') {
      var sources = picture.querySelectorAll('source[data-srcset]');
      for (var i = 0; i < sources.length; i++) {
        sources[i].setAttribute('srcset', sources[i].getAttribute('data-srcset'));
        sources[i].removeAttribute('data-srcset');
        changed = true;
      }
    }
    return changed;
  }

  function makeEager(img) {
    promoteImgSources(img);
    if (img.getAttribute('loading') !== 'eager') img.setAttribute('loading', 'eager');
    if (!img.getAttribute('fetchpriority')) img.setAttribute('fetchpriority', 'high');
    try { if (img.decode) img.decode().catch(function () {}); } catch (e) {}
  }

  function prepImg(img) {
    if (img.dataset.fmbImg) return;
    img.dataset.fmbImg = '1';
    if (img.hasAttribute('data-no-fast')) return;

    // Async decode never hurts and avoids blocking paint.
    if (!img.getAttribute('decoding')) img.setAttribute('decoding', 'async');

    // In (or near) the first viewport -> load it now, at high priority.
    var rect;
    try { rect = img.getBoundingClientRect(); } catch (e) { rect = null; }
    var inFold = rect && rect.top < vh() * EAGER_FACTOR && rect.bottom > -200;

    if (inFold) {
      makeEager(img);
    } else if (img.dataset.src || img.dataset.srcset) {
      // Below the fold but JS-lazy -> promote a bit before it enters view.
      var io = getImgObserver();
      if (io) io.observe(img); else promoteImgSources(img);
    }
  }

  function getImgObserver() {
    if (imgIO || !('IntersectionObserver' in window)) return imgIO;
    imgIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          var img = e.target;
          promoteImgSources(img);
          try { if (img.decode) img.decode().catch(function () {}); } catch (err) {}
          imgIO.unobserve(img);
        }
      });
    }, { root: null, rootMargin: NEAR_VIEW_PX + 'px', threshold: 0.01 });
    return imgIO;
  }

  function scanImages(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var imgs = scope.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) prepImg(imgs[i]);
  }

  /* ---------------- VIDEOS ---------------- */

  function isVideoTarget(v) {
    if (!v || v.tagName !== 'VIDEO') return false;
    if (v.hasAttribute('data-no-autoplay')) return false;
    var wantsAutoplay = v.hasAttribute('autoplay') || v.getAttribute('data-autoplay') === 'true';
    if (v.hasAttribute('controls') && !wantsAutoplay) return false;
    return true;
  }

  function ensureVideoSources(v) {
    if (v.dataset.fmbLoaded) return false;
    var changed = false;
    var lazy = v.querySelectorAll('source[data-src]');
    for (var i = 0; i < lazy.length; i++) {
      lazy[i].setAttribute('src', lazy[i].getAttribute('data-src'));
      lazy[i].removeAttribute('data-src');
      changed = true;
    }
    if (v.hasAttribute('data-src') && !v.getAttribute('src')) {
      v.setAttribute('src', v.getAttribute('data-src'));
      v.removeAttribute('data-src');
      changed = true;
    }
    if (changed) { v.dataset.fmbLoaded = '1'; try { v.load(); } catch (e) {} }
    return changed;
  }

  function prepVideo(v) {
    if (v.dataset.fmbPrep) return;
    v.dataset.fmbPrep = '1';
    v.muted = true; v.defaultMuted = true; v.setAttribute('muted', '');
    v.playsInline = true; v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    if (!v.hasAttribute('loop')) { v.setAttribute('loop', ''); v.loop = true; }
    v.setAttribute('autoplay', '');
    v.addEventListener('pause', function () {
      if (v._fmbAuto) return;
      if (document.hidden || v.ended) return;
      v._fmbUserPaused = true;
    });
    v.addEventListener('play', function () { v._fmbUserPaused = false; });
  }

  function attemptPlay(v) {
    if (v._fmbUserPaused) return;
    ensureVideoSources(v);
    var p;
    try { p = v.play(); } catch (e) { queue(v); return; }
    if (p && typeof p.then === 'function') p.catch(function () { queue(v); });
  }

  function queue(v) { if (pending.indexOf(v) === -1) pending.push(v); }

  function flushPending() {
    if (!pending.length) return;
    var list = pending.slice(); pending.length = 0;
    list.forEach(function (v) {
      if (v._fmbUserPaused) return;
      ensureVideoSources(v);
      var p; try { p = v.play(); } catch (e) {}
      if (p && typeof p.then === 'function') p.catch(function () { queue(v); });
    });
  }

  function autoPause(v) {
    if (v.paused) return;
    v._fmbAuto = true;
    try { v.pause(); } catch (e) {}
    setTimeout(function () { v._fmbAuto = false; }, 0);
  }

  function getVideoObserver() {
    if (vidIO || !('IntersectionObserver' in window)) return vidIO;
    vidIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) attemptPlay(e.target); else autoPause(e.target);
      });
    }, { root: null, rootMargin: '300px', threshold: 0.1 });
    return vidIO;
  }

  function registerVideo(v) {
    if (!isVideoTarget(v) || v.dataset.fmbReg) return;
    v.dataset.fmbReg = '1';
    prepVideo(v);
    var io = getVideoObserver();
    if (io) io.observe(v); else attemptPlay(v);
  }

  function scanVideos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var vids = scope.querySelectorAll('video');
    for (var i = 0; i < vids.length; i++) registerVideo(vids[i]);
  }

  /* ---------------- INIT ---------------- */

  function scan(root) { scanImages(root); scanVideos(root); }

  function init() {
    scan(document);

    if ('MutationObserver' in window) {
      var mo = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          var nodes = muts[i].addedNodes;
          for (var j = 0; j < nodes.length; j++) {
            var n = nodes[j];
            if (n.nodeType !== 1) continue;
            if (n.tagName === 'IMG') prepImg(n);
            else if (n.tagName === 'VIDEO') registerVideo(n);
            else if (n.querySelectorAll) scan(n);
          }
        }
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }

    var opts = { passive: true };
    ['pointerdown', 'touchstart', 'keydown', 'scroll', 'click'].forEach(function (evt) {
      window.addEventListener(evt, flushPending, opts);
    });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) flushPending();
    });
    window.addEventListener('pageshow', flushPending);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.FastMediaBoost = { scan: scan, flush: flushPending };
})();
